import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { clearAuth, useAuth } from '@/features/auth/authStore';
import { switchOptions as reportSwitchOptions, type DashboardConfig } from '@/features/dashboard/config';
import { useAhReport } from '@/features/ah/useAhReport';
import type { AhVenue } from '@/features/ah/ahReport';
import { formatRelativeNL, SECTIONS } from '@/features/ah/utils';
import { TopBar } from '@/features/ah/components/TopBar';
import { SubNav } from '@/features/ah/components/SubNav';
import { PeriodStrip } from '@/features/ah/components/PeriodStrip';
import { Hero } from '@/features/ah/components/Hero';
import { KPIGrid } from '@/features/ah/components/KPIGrid';
import { EngagementSection } from '@/features/ah/components/EngagementSection';
import { VenuesSection } from '@/features/ah/components/VenuesSection';
import { BehaviourSection } from '@/features/ah/components/BehaviourSection';
import { VenueDrawer } from '@/features/ah/components/VenueDrawer';
import '@/features/ah/ah.css';

export function AhDashboardPage({ config }: { config: DashboardConfig }) {
  const auth = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data, isPreview, lastRefreshedAt } = useAhReport(config.reportId, config.apiBaseUrl);

  const [activeSection, setActiveSection] = useState('overview');
  const [drawerVenue, setDrawerVenue] = useState<AhVenue | null>(null);

  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNowMs(Date.now()), 60_000);
    return () => clearInterval(id);
  }, []);
  const updatedLabel = formatRelativeNL(lastRefreshedAt, nowMs) ?? '—';

  const days = data.days.map((d) => new Date(d));

  useEffect(() => {
    const onScroll = () => {
      const above = SECTIONS.map((s) => {
        const el = document.getElementById(s.id);
        return { id: s.id, top: el ? el.getBoundingClientRect().top : Infinity };
      }).filter((p) => p.top < 200);
      if (above.length > 0) setActiveSection(above[above.length - 1].id);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setDrawerVenue(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const switchOptions = reportSwitchOptions(auth?.allowedReports ?? [], auth?.role === 'admin', config.reportId);

  function onSignOut() {
    clearAuth();
    queryClient.clear();
    navigate('/login', { replace: true });
  }

  return (
    <div className="ah-root">
      <div className="app">
        <TopBar
          campaign={data.campaign}
          updatedLabel={updatedLabel}
          isPreview={isPreview}
          onSwitch={(p) => navigate(p)}
          switchOptions={switchOptions}
          onSignOut={onSignOut}
          isAdmin={auth?.role === 'admin'}
          onManageUsers={() => navigate('/admin/users')}
        />
        <SubNav active={activeSection} setActive={setActiveSection} />
        <main className="main">
          <section id="overview">
            <Hero campaign={data.campaign} totals={data.totals} />
            <PeriodStrip campaign={data.campaign} />
            <KPIGrid data={data} />
          </section>
          <EngagementSection data={data} days={days} />
          <VenuesSection venues={data.venues} onOpen={setDrawerVenue} overlap={data.venueOverlap} crossVenue={data.crossVenue} period="all" />
          <BehaviourSection menuOpens={data.menuOpens} />
          <div className="footer">
            {data.campaign.name} · Bijgewerkt {updatedLabel} · Data wordt elke 30–60 minuten ververst
          </div>
        </main>
        <VenueDrawer venue={drawerVenue} onClose={() => setDrawerVenue(null)} />
      </div>
    </div>
  );
}
